import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  CheckCircle, 
  Shield, 
  Clock, 
  Star,
  Zap,
  Droplet,
  Palette,
  Hammer,
  HardHat,
  Home,
  Grid3x3,
  Key,
  Sparkles,
  Bug,
  Waves,
  ArrowRight,
  MapPin
} from "lucide-react";
import { getSuburbSuggestions } from "@/data/australianSuburbs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  "zap": Zap,
  "droplet": Droplet,
  "palette": Palette,
  "hammer": Hammer,
  "hard-hat": HardHat,
  "home": Home,
  "grid-3x3": Grid3x3,
  "key": Key,
  "sparkles": Sparkles,
  "bug": Bug,
  "waves": Waves,
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Location autocomplete state
  const [locationInput, setLocationInput] = useState("");
  const [selectedSuburb, setSelectedSuburb] = useState("");
  const [selectedPostcode, setSelectedPostcode] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  const handleLocationInputChange = (value) => {
    setLocationInput(value);
    
    if (value.length >= 2) {
      const suggestions = getSuburbSuggestions(value);
      setLocationSuggestions(suggestions);
      setShowLocationSuggestions(suggestions.length > 0);
    } else {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    }
  };

  const handleLocationSelect = (suburb, postcode, state) => {
    setLocationInput(`${suburb} ${postcode}`);
    setSelectedSuburb(suburb);
    setSelectedPostcode(postcode);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.append('service', searchQuery);
    
    // Use selected suburb if available, otherwise parse the raw location input
    if (selectedSuburb) {
      params.append('suburb', selectedSuburb);
      if (selectedPostcode) params.append('postcode', selectedPostcode);
    } else if (locationInput.trim()) {
      // Parse raw input - check if it contains a postcode
      const trimmedInput = locationInput.trim();
      const postcodeMatch = trimmedInput.match(/\d{4}$/);
      if (postcodeMatch) {
        const postcode = postcodeMatch[0];
        const suburb = trimmedInput.replace(postcode, '').trim();
        if (suburb) params.append('suburb', suburb);
        params.append('postcode', postcode);
      } else {
        params.append('suburb', trimmedInput);
      }
    }
    navigate(`/browse?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Local Tradies Online | Find Trusted Tradies Across Australia</title>
        <meta name="description" content="Find trusted local tradies across Australia. Electricians, plumbers, builders, carpenters and more. Get free quotes from verified professionals in your area." />
        <link rel="canonical" href="https://localtradiesonline.com.au" />
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-brand-mesh pt-20" data-testid="hero-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-8 py-12">
              <div className="inline-block">
                <div className="bg-white/80 backdrop-blur-sm border border-orange-200 rounded-full px-4 py-2 text-sm font-medium text-orange-600">
                  Australia's #1 Tradie Directory
                </div>
              </div>
              
              <h1 
                className="text-5xl md:text-7xl font-bold tracking-tight leading-none text-[#0f172a]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="hero-heading"
              >
                Find Trusted <span className="text-[#f97316]">Local Tradies</span> Fast
              </h1>
              
              <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-xl">
                Connect with verified, insured Australian tradespeople in your area. 
                Get quotes from electricians, plumbers, builders, and more - all in minutes.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="bg-white/80 backdrop-blur-xl border border-white/20 shadow-2xl rounded-3xl p-4 space-y-3" data-testid="hero-search-form">
                <div className="flex flex-col md:flex-row gap-3">
                  <Input
                    type="text"
                    placeholder="Trade or Service"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-14 md:h-16 flex-1 min-w-[200px] rounded-full border-2 border-slate-200 bg-white px-6 text-base"
                    data-testid="hero-search-service-input"
                  />
                  {/* Smart Location Input with Autocomplete */}
                  <div className="relative w-full md:w-48">
                    <Input
                      type="text"
                      placeholder="Location"
                      value={locationInput}
                      onChange={(e) => handleLocationInputChange(e.target.value)}
                      onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                      onFocus={() => locationInput.length >= 2 && locationSuggestions.length > 0 && setShowLocationSuggestions(true)}
                      className="h-14 md:h-16 w-full rounded-full border-2 border-slate-200 bg-white px-6 text-base"
                      data-testid="hero-search-location-input"
                    />
                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-60 overflow-auto">
                        {locationSuggestions.map((item, index) => (
                          <div
                            key={index}
                            className="px-4 py-3 hover:bg-orange-50 cursor-pointer border-b border-slate-100 last:border-b-0 first:rounded-t-2xl last:rounded-b-2xl"
                            onMouseDown={() => handleLocationSelect(item.suburb, item.postcode, item.state)}
                            data-testid={`hero-location-suggestion-${index}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <MapPin className="w-4 h-4 text-[#f97316] mr-2" />
                                <span className="font-medium text-[#0f172a]">{item.suburb}</span>
                                <span className="text-slate-500 ml-2">{item.state}</span>
                              </div>
                              <span className="text-sm font-mono text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{item.postcode}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button 
                    type="submit"
                    className="h-14 md:h-16 bg-[#f97316] hover:bg-[#ea580c] text-white rounded-full px-8 font-bold text-lg shadow-lg"
                    data-testid="hero-search-submit-btn"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    Search
                  </Button>
                </div>
              </form>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-6 pt-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-slate-700">Verified Tradies</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-slate-700">Fully Insured</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-orange-600" />
                  <span className="text-sm font-medium text-slate-700">Quality Rated</span>
                </div>
              </div>
            </div>

            {/* Right: Image */}
            <div className="relative hidden lg:block">
              <div className="relative z-10">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/28a7e231-ff1b-4b8f-915f-47798802b078/images/98a588ab105cf336283091906d92c8d47ba580b2764bd88cb67cb34e75b34498.png"
                  alt="Australian Tradie with Hard Hat and Hi-Vis Vest"
                  className="rounded-3xl shadow-2xl w-full object-cover"
                  style={{ height: '600px' }}
                />
              </div>
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-orange-200 rounded-full blur-3xl opacity-30"></div>
              <div className="absolute -top-8 -left-8 w-64 h-64 bg-blue-200 rounded-full blur-3xl opacity-30"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Services Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl md:text-5xl font-bold tracking-tight text-[#0f172a] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Popular Services
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              From electrical work to pool maintenance, find the right tradie for any job
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {services.slice(0, 10).map((service) => {
              const IconComponent = iconMap[service.icon] || Hammer;
              return (
                <Link
                  key={service.id}
                  to={`/browse?service=${service.slug}`}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-6 text-center"
                  data-testid={`service-card-${service.slug}`}
                >
                  <div className="flex flex-col items-center space-y-3">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                      <IconComponent className="w-6 h-6 text-[#f97316]" />
                    </div>
                    <h3 className="font-semibold text-[#0f172a] text-sm">{service.name}</h3>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Link 
              to="/browse"
              className="inline-flex items-center text-[#f97316] hover:text-[#ea580c] font-bold transition-colors"
              data-testid="view-all-services-link"
            >
              View All Services <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="how-it-works-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl md:text-5xl font-bold tracking-tight text-[#0f172a] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              How It Works
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Get connected with quality tradies in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-4" data-testid="step-1">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#f97316] text-white text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                1
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Tell Us What You Need
              </h3>
              <p className="text-slate-600">
                Describe your job and where you're located. It takes just 2 minutes.
              </p>
            </div>

            <div className="text-center space-y-4" data-testid="step-2">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#f97316] text-white text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                2
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Browse & Compare
              </h3>
              <p className="text-slate-600">
                View profiles, ratings, and reviews from verified tradies in your area.
              </p>
            </div>

            <div className="text-center space-y-4" data-testid="step-3">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#f97316] text-white text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                3
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Hire with Confidence
              </h3>
              <p className="text-slate-600">
                Choose the best tradie for your job. All work is quality guaranteed.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/get-quote"
              className="inline-block bg-[#f97316] text-white rounded-full px-8 py-4 font-bold hover:bg-[#ea580c] transition-all shadow-lg hover:shadow-orange-500/25 active:scale-95"
              data-testid="get-started-btn"
            >
              Get Started Now
            </Link>
          </div>
        </div>
      </section>

      {/* CTA for Tradies */}
      <section className="py-16 md:py-24 bg-[#0f172a] text-white" data-testid="tradie-cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 
                className="text-4xl md:text-5xl font-bold tracking-tight mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Are You a Tradie?
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Join Australia's fastest-growing tradie directory. Get high-quality leads, 
                grow your business, and connect with customers who need your skills.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-[#f97316] flex-shrink-0" />
                  <span>Quality leads delivered daily</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-[#f97316] flex-shrink-0" />
                  <span>Build your online reputation</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-[#f97316] flex-shrink-0" />
                  <span>Simple pricing, no hidden fees</span>
                </li>
              </ul>
              <Link 
                to="/join"
                className="inline-block bg-[#f97316] text-white rounded-full px-8 py-4 font-bold hover:bg-[#ea580c] transition-all shadow-lg active:scale-95"
                data-testid="join-as-tradie-btn"
              >
                Join as a Tradie
              </Link>
            </div>

            <div className="relative hidden lg:block">
              <img
                src="https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/luk6t0fi_tradies%20tradie-working-on-house-building-site-austockphoto-000023842.jpg"
                alt="Australian tradies working on house building site"
                className="rounded-3xl shadow-2xl w-full object-cover"
                style={{ height: '500px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white" data-testid="stats-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#f97316] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                10,000+
              </div>
              <div className="text-slate-600">Verified Tradies</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#f97316] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                50,000+
              </div>
              <div className="text-slate-600">Jobs Completed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#f97316] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                4.8★
              </div>
              <div className="text-slate-600">Average Rating</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#f97316] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                24/7
              </div>
              <div className="text-slate-600">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}